import Fuse from "fuse.js";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { Input } from "../../components/ui/Input";
import { IconSearch, IconX } from "@tabler/icons-react";
import Loader from "../../components/Loader";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import type { OnboardingTag } from "../../domains/auth/hooks/useSignupOnboardingState";
import { apiClient, revalidateApiQuery, useApiQuery } from "../../services/api";
import { HapticEffect } from "../../interfaces";
import { gaEventUserUpdateInterests } from "../../tools/analytics/domains/user.analytics";
import { iosGenerateHaptic } from "../../tools/ios";
import {
  HIDDEN_SPECIALTY_IDS,
  UNAVAILABLE_SPECIALTIES,
} from "../../tools/utils";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

export default function ManageSpecialtiesPage() {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(true);
  const [medicalSpecialties, setMedicalSpecialties] =
    useState<OnboardingTag[]>();
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<OnboardingTag[] | null>(
    null
  );
  const subspecialtiesQuery = useApiQuery("/webapp/public/tags", {
    durable: true,
  });
  const medicalSubspecialties =
    subspecialtiesQuery.state === "success" ? subspecialtiesQuery.body : [];
  const [selections, setSelections] = useState<OnboardingTag[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialSelections, setInitialSelections] = useState<OnboardingTag[]>(
    []
  );

  useEffect(() => {
    if (currentUser && subspecialtiesQuery.state === "success") initialize();
  }, [currentUser, subspecialtiesQuery.state]);

  async function initialize() {
    try {
      await handleFetchSpecialties();
      setIsLoading(false);
    } catch (error) {
      console.error("Error during initialization", error);
    }
  }

  async function handleFetchSpecialties() {
    if (!currentUser) await reloadCurrentUser();
    if (!currentUser) return;

    const { professionModel, mainSpecialtyModel, medicalSpecialtyModels } =
      currentUser;
    const professionMedicalSpecialtyIds =
      professionModel === null ? [] : professionModel.medical_specialties;
    const professionSpecialties = medicalSubspecialties
      .filter((specialty) =>
        professionMedicalSpecialtyIds.includes(specialty.id)
      )
      .filter((specialty) => !HIDDEN_SPECIALTY_IDS.has(specialty.id));

    setMedicalSpecialties(
      professionSpecialties.filter(
        (specialty) => specialty.uid !== mainSpecialtyModel?.uid
      )
    );
    setSelections(medicalSpecialtyModels);
    setInitialSelections(medicalSpecialtyModels);
  }

  function handleSearch(value: string) {
    if (value.length > 0 && medicalSpecialties) {
      const options = {
        includeScore: true,
        threshold: 0.25,
        keys: [`translations.${activeLang}`],
        ignoreDiacritics: true,
      };

      const fuse = new Fuse(medicalSpecialties, options);
      const results = fuse.search(value).map((res) => res.item);
      setSearchResults(results);
    } else {
      setSearchResults(null);
    }
  }

  function handleSelect(specialty: OnboardingTag | "all") {
    iosGenerateHaptic(HapticEffect.SELECTION);

    if (specialty === "all") {
      const professionMedicalSpecialtyIds =
        currentUser?.professionModel === null ||
        currentUser?.professionModel === undefined
          ? []
          : currentUser.professionModel.medical_specialties;
      const professionSpecialties = medicalSubspecialties
        .filter((tag) => professionMedicalSpecialtyIds.includes(tag.id))
        .filter((tag) => !HIDDEN_SPECIALTY_IDS.has(tag.id));
      setSelections(
        selections.length === professionSpecialties.length
          ? []
          : [...professionSpecialties]
      );
      return;
    }

    setSelections((prev) =>
      prev.map((el) => el.uid).includes(specialty.uid)
        ? prev.filter((el) => el.uid !== specialty.uid)
        : [...prev, specialty]
    );
  }

  async function handleSubmit() {
    try {
      setIsSubmitting(true);
      const medical_specialties = selections.map((spe) => spe.id);
      const tags = medicalSubspecialties
        .filter((tag) => {
          if (
            currentUser?.professionModel === null ||
            currentUser?.professionModel === undefined
          ) {
            return false;
          }

          return (
            tag.parent !== null &&
            currentUser.professionModel.medical_specialties.includes(
              tag.parent
            ) &&
            medical_specialties.includes(tag.parent)
          );
        })
        .map((tag) => tag.id);

      await apiClient.patch("/webapp/private/users/me", {
        json: { medical_specialties, tags },
      });
      void revalidateApiQuery("/webapp/private/playlists/recommended");
      reloadCurrentUser();

      gaEventUserUpdateInterests(selections);

      displayToast(
        selections.length > 1
          ? t("Specialties updated. We'll tune your recommendations.")
          : t("Specialty updated. We'll tune your recommendations."),
        "success"
      );

      setTimeout(() => {
        history.goBack();
      }, 500);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  if (!currentUser || isLoading) return <Loader expanded />;

  const hideFooter =
    selections?.map((el) => el.uid).toString() ===
    currentUser.medicalSpecialtyModels.map((el) => el.uid).toString();

  const allSelected =
    currentUser &&
    selections.length ===
      (currentUser.professionModel === null
        ? 0
        : currentUser.professionModel.medical_specialties.filter(
            (id) => !HIDDEN_SPECIALTY_IDS.has(id)
          ).length);

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent />}
    >
      <div className="pb-[100px]">
        {medicalSpecialties ? (
          <div>
            <div className="mx-[21px] my-[32px] text-[16px] font-bold leading-[120%] text-secondary-bluegrey-7">
              {t("Add your favorite specialties")}
            </div>

            <div className="mx-[21px]">
              <Input
                value={searchValue}
                placeholder={t("Search")}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  handleSearch(e.target.value);
                }}
                trailing={
                  searchValue.length === 0 ? (
                    <IconSearch className="size-5 text-ds-slate-400" />
                  ) : (
                    <button
                      type="button"
                      aria-label={t("Clear search")}
                      className="flex size-8 items-center justify-center rounded-full text-ds-slate-400 hover:text-ds-black"
                      onClick={() => {
                        setSearchValue("");
                        setSearchResults(null);
                      }}
                    >
                      <IconX className="size-4" />
                    </button>
                  )
                }
              />
            </div>

            <div className="mx-[21px] mt-[32px] text-[21px] font-bold leading-[100%] text-secondary-raspberry-1">
              {t("Default")}
            </div>
            <div className="p-[22px] flex flex-col">
              <div className="py-[20px] flex items-center justify-between gap-[20px]">
                <div className="flex items-center font-bold text-[15px] leading-[130%] text-primary-black">
                  {t("All specialties")}
                </div>
                <Button
                  variant="outline"
                  size="md"
                  className={`w-fit font-semibold ${allSelected ? "bg-secondary-soft-yellow-2! border-secondary-orange-2! text-secondary-orange-2!" : ""}`}
                  onClick={() => {
                    handleSelect("all");
                  }}
                >
                  {allSelected ? t("Selected") : t("Select")}
                </Button>
              </div>
            </div>

            <div className="mx-[21px] mt-[32px] text-[21px] font-bold leading-[100%] text-secondary-raspberry-1">
              {t("Specialties")}
            </div>
            <div className="p-[22px] flex flex-col">
              {(searchResults ?? medicalSpecialties)
                ?.sort((a, b) => {
                  const aSelected = initialSelections.some(
                    (s) => s.uid === a.uid
                  );
                  const bSelected = initialSelections.some(
                    (s) => s.uid === b.uid
                  );
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;

                  return getItemTranslation(a).localeCompare(
                    getItemTranslation(b)
                  );
                })
                .filter(
                  (el) =>
                    !UNAVAILABLE_SPECIALTIES.includes(
                      getItemTranslation(el, "en")
                    )
                )
                .map((specialty, index) => {
                  const isLastRow =
                    index ===
                    (searchResults ?? medicalSpecialties)?.filter(
                      (el) =>
                        !UNAVAILABLE_SPECIALTIES.includes(
                          getItemTranslation(el, "en")
                        )
                    ).length -
                      1;

                  return (
                    <div
                      className={`py-[20px] flex items-center justify-between gap-[20px] border-b border-primary-black ${isLastRow ? "border-b-transparent" : ""}`}
                      key={specialty?.id + "--home-specialties-row"}
                    >
                      <div className="flex items-center font-bold text-[15px] leading-[130%] text-primary-black">
                        {getItemTranslation(specialty)}
                      </div>
                      <Button
                        variant="outline"
                        size="md"
                        onClick={() => {
                          handleSelect(specialty);
                        }}
                        className={`w-fit font-semibold ${selections.find((el) => el.uid === specialty.uid) ? "bg-secondary-soft-yellow-2! border-secondary-orange-2! text-secondary-orange-2!" : ""}`}
                      >
                        {selections.find((el) => el.uid === specialty.uid)
                          ? t("Selected")
                          : t("Select")}
                      </Button>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : (
          <Loader expanded />
        )}
        <div
          className={`fixed bottom-0 inset-x-0  p-[21px] pb-[50px] bg-white transform translate-y-0 transition-transform ease-out duration-300 ${hideFooter ? "translate-y-[140px]" : ""}`}
        >
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {t("Save favorites")}
          </Button>
        </div>
      </div>
    </ScrollableLayout>
  );
}
